'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { resolveLocale } from '@/lib/date-utils';
import { cn, formatDate } from '@/lib/utils';
type PropsType = Omit<React.ComponentPropsWithoutRef<'button'>, 'value' | 'onChange'> & {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  isInvalid?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  locale?: string;
};
export function DatePicker(t0) {
  const $ = _c(38);
  let disabled;
  let isInvalid;
  let onChange;
  let placeholder;
  let props;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      placeholder,
      isInvalid,
      autoFocus: t1,
      disabled,
      locale: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = isInvalid;
    $[3] = onChange;
    $[4] = placeholder;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = value;
  } else {
    disabled = $[1];
    isInvalid = $[2];
    onChange = $[3];
    placeholder = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    value = $[8];
  }
  const autoFocus = t1 === undefined ? false : t1;
  const locale = t2 === undefined ? "en-US" : t2;
  const t = useTranslations();
  let t3;
  if ($[9] !== locale) {
    t3 = resolveLocale(locale);
    $[9] = locale;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const dateFnsLocale = t3;
  const t4 = !value && "text-muted-foreground";
  const t5 = isInvalid && "border-destructive focus-visible:ring-destructive";
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = cn("h-9 w-full justify-start gap-1 text-left font-normal", t4, t5);
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CalendarIcon className="mr-1 h-4 w-4 shrink-0" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== locale || $[16] !== placeholder || $[17] !== t || $[18] !== value) {
    t8 = value ? formatDate(value, locale) : <span className="text-muted-foreground">{placeholder ?? t("pick_a_date")}</span>;
    $[15] = locale;
    $[16] = placeholder;
    $[17] = t;
    $[18] = value;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <span className="flex-1">{t8}</span>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== disabled || $[24] !== props || $[25] !== t6 || $[26] !== t9) {
    t11 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" disabled={disabled} className={t6} {...props}>{t7}{t9}{t10}</Button></PopoverTrigger>;
    $[23] = disabled;
    $[24] = props;
    $[25] = t6;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = new Date(1900, 0);
    t13 = new Date(new Date().getFullYear(), 11);
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  let t14;
  if ($[30] !== autoFocus || $[31] !== dateFnsLocale || $[32] !== onChange || $[33] !== value) {
    t14 = <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={value} defaultMonth={value} onSelect={onChange} autoFocus={autoFocus} captionLayout="dropdown" startMonth={t12} endMonth={t13} locale={dateFnsLocale} /></PopoverContent>;
    $[30] = autoFocus;
    $[31] = dateFnsLocale;
    $[32] = onChange;
    $[33] = value;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11 || $[36] !== t14) {
    t15 = <Popover>{t11}{t14}</Popover>;
    $[35] = t11;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}